import React, { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import GaugeComponent from 'react-gauge-component'
import _ from 'lodash'
import { selectGunbotAllAnalysis, selectAllPnlHistory } from 'src/store/bots'

const ACCENT = '#4E8FC6'
const BLUE_SCALE = ['#223041', '#2C4E6E', '#376B99', '#4E8FC6', '#6EB0E6']
const roiTickers = [
  { value: 0.5, label: 'Very bad' },
  { value: 1.5, label: 'Bad' },
  { value: 2.5, label: 'Normal' },
  { value: 3.5, label: 'Good' },
  { value: 4.5, label: 'Very Good' },
]

// ---- Tooltip definitions ----
const KPI_DESCRIPTIONS: Record<string, string> = {
  ROI: 'Return on Investment — measures performance relative to capital used.',
  RR: 'Risk/Reward — compares expected profit vs risk taken.',
  CP: 'Capital Utilization Efficiency — shows how effectively funds are allocated.',
  pnl: 'Realized profit and loss over the selected timeframe.',
  profitFactor: 'Ratio between gross profits and gross losses; >1 means profitable system.',
  winRate: 'Percentage of trades closed in profit.',
  avgTradeDuration: 'Average holding time per trade, helps identify strategy speed.',
  sharpeRatio: 'Risk-adjusted return indicator, higher is better.',
  drawdown: 'Largest equity drop from a peak, helps measure risk exposure.',
  exposure: 'Percent of capital currently used in open trades.',
}

export default function AnalysisPage() {
  const analysis = useSelector(selectGunbotAllAnalysis, _.isEqual)
  const pnlHistory = useSelector(selectAllPnlHistory)
  const coremems = useSelector((s: any) => s.bots.coremems)

  const [selectedTf] = useState<'today' | 'weekAgo' | 'monthAgo' | 'yearAgo'>('yearAgo')

  // ---- SUM totalPnl across ALL connected bots for the selected timeframe ----
  const { totalPnl } = useMemo(() => {
    let sum = 0
    const bots = Object.values(pnlHistory || {})
    for (const bot of bots) {
      const tfData = (bot as any)?.[selectedTf]
      if (tfData && typeof tfData.pnl === 'number' && Number.isFinite(tfData.pnl)) {
        sum += tfData.pnl
      }
    }
    return { totalPnl: sum }
  }, [pnlHistory, selectedTf])

  const { allROI, allRR, allCP, totalAssets } = analysis || {}
  const [selectedExchange, setSelectedExchange] = useState('')
  const [selectedPair, setSelectedPair] = useState('')

  const kpiMap = useMemo(() => {
    const map: Record<string, Record<string, any>> = {}
    _.each(coremems, ({ memory }) => {
      _.each(memory, (entry) => {
        const ex = entry.exchangeName
        const pair = entry.pairName
        const kpis = entry.kpis
        if (!ex || !pair || !kpis) return
        if (!map[ex]) map[ex] = {}
        map[ex][pair] = kpis
      })
    })
    return map
  }, [coremems])

  const exchanges = Object.keys(kpiMap)
  const pairs = selectedExchange ? Object.keys(kpiMap[selectedExchange] || {}) : []
  const selectedEntry =
  selectedExchange && selectedPair
    ? kpiMap[selectedExchange]?.[selectedPair]
    : null

const activeKpis =
  (selectedEntry?.kpis && typeof selectedEntry.kpis === 'object')
    ? selectedEntry.kpis
    : selectedEntry || {}

const activeMeta =
  (selectedEntry?.meta && typeof selectedEntry.meta === 'object')
    ? selectedEntry.meta
    : null


  const storedOrderKey = `${selectedExchange}_${selectedPair}_kpiOrder`
  const [kpiOrder, setKpiOrder] = useState<string[]>([])

  useEffect(() => {
    if (Object.keys(activeKpis).length) {
      const stored = localStorage.getItem(storedOrderKey)
      const initial = stored ? JSON.parse(stored) : Object.keys(activeKpis)
      setKpiOrder(initial)
    }
  }, [selectedExchange, selectedPair, activeKpis])

  const onDragStart = (e: React.DragEvent<HTMLDivElement>, key: string) =>
    e.dataTransfer.setData('text/plain', key)
  const onDrop = (e: React.DragEvent<HTMLDivElement>, targetKey: string) => {
    const draggedKey = e.dataTransfer.getData('text/plain')
    if (!draggedKey || draggedKey === targetKey) return
    const order = [...kpiOrder]
    const from = order.indexOf(draggedKey)
    const to = order.indexOf(targetKey)
    if (from === -1 || to === -1) return
    order.splice(from, 1)
    order.splice(to, 0, draggedKey)
    setKpiOrder(order)
    localStorage.setItem(storedOrderKey, JSON.stringify(order))
  }
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault()

  const fmt = (num: number, d = 6) =>
    Number.isFinite(num) ? parseFloat(num.toFixed(d)).toString() : ''

  return (
    <div className='p-6 text-gray-200 space-y-10'>
      {/* --- TOP GAUGES (UNTOUCHED) --- */}
      <div className='flex flex-wrap-reverse justify-evenly gap-4'>
        <div className='w-full rounded-lg 2xs:w-auto xl:w-1/4 3xl:w-1/5'>
          <GaugeComponent
            value={allROI}
            maxValue={5}
            marginInPercent={{ top: 0.07, bottom: 0, left: 0.07, right: 0.07 }}
            type='radial'
            labels={{
              valueLabel: { style: { fill: 'white' }, formatTextValue: (v) => String(v) },
              tickLabels: {
                type: 'inner',
                ticks: roiTickers,
                hideMinMax: true,
                defaultTickValueConfig: {
                  style: { color: 'white', fontSize: '14px' },
                  formatTextValue(val) {
                    return roiTickers.find(({ value }) => value === val)?.label || ''
                  },
                },
              },
            }}
            arc={{
              cornerRadius: 7,
              colorArray: BLUE_SCALE,
              subArcs: [{ limit: 1 }, { limit: 2 }, { limit: 3 }, { limit: 4 }, { limit: 5 }],
              padding: 0.03,
              width: 0.15,
              gradient: false,
            }}
            pointer={{ elastic: true, color: ACCENT, type: 'needle', width: 15, length: 0.5 }}
          />
          <div className='text-center text-subtitle'>ROI</div>
        </div>

        <div className='w-full rounded-lg 2xs:w-auto xl:w-1/4 3xl:w-1/5'>
          <GaugeComponent
            type='radial'
            maxValue={5}
            marginInPercent={{ top: 0.075, bottom: 0, left: 0.07, right: 0.07 }}
            labels={{
              tickLabels: {
                type: 'inner',
                ticks: [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }, { value: 5 }],
                defaultTickValueConfig: { style: { color: 'white', fontSize: '14px' } },
              },
            }}
            arc={{
              gradient: false,
              subArcs: [{ limit: 1 }, { limit: 2 }, { limit: 3 }, { limit: 4 }, { limit: 5 }],
              colorArray: BLUE_SCALE,
              padding: 0,
              width: 0.15,
            }}
            pointer={{ elastic: true, color: ACCENT, width: 15, length: 0.5 }}
            value={analysis?.allRR}
          />
          <div className='text-center text-subtitle'>Risk / Reward</div>
        </div>

        <div className='w-full rounded-lg 2xs:w-auto xl:w-1/4 3xl:w-1/5'>
          <GaugeComponent
            value={allCP}
            maxValue={100}
            type='radial'
            labels={{
              tickLabels: {
                type: 'inner',
                ticks: [{ value: 20 }, { value: 40 }, { value: 60 }, { value: 80 }, { value: 100 }],
                defaultTickValueConfig: { style: { color: 'white', fontSize: '14px' } },
              },
              valueLabel: { style: { fill: 'white' } },
            }}
            arc={{
              colorArray: BLUE_SCALE,
              padding: 0,
              cornerRadius: 0,
              width: 0.15,
              subArcs: [
                { limit: 20 },
                { limit: 40 },
                { limit: 60 },
                { limit: 80 },
                { limit: 100 },
              ],
              gradient: false,
            }}
            pointer={{ elastic: true, color: ACCENT, type: 'needle', width: 15, length: 0.5 }}
          />
          <div className='text-center text-subtitle'>Capital Utilization Efficiency</div>
          <div className='text-center text-subtitle'>{`(Total: ${totalAssets?.toFixed?.(2)} $)`}</div>
        </div>
      </div>

      {/* --- SUMMARY --- */}
      <div className='mt-8 text-center text-gray-400 text-sm'>
        Total Portfolio Value:{' '}
        <span className='text-gray-200'>{totalAssets?.toFixed?.(2)} USDT</span>
        <br />
        Total Realized PnL: <span className='text-green-400'>{totalPnl.toFixed(2)} USDT</span>
      </div>
      
      {/* --- SELECTORS --- */}
      <div className='flex justify-center gap-6 mt-10'>
        <select
          value={selectedExchange}
          onChange={(e) => {
            setSelectedExchange(e.target.value)
            setSelectedPair('')
          }}
          className='appearance-none bg-[#111] border border-[#2a2a2a] text-gray-300 rounded-lg px-4 py-2.5 outline-none focus:border-[#4E8FC6]'
        >
          <option value=''>Select Exchange</option>
          {exchanges.map((ex) => (
            <option key={ex} value={ex}>
              {ex}
            </option>
          ))}
        </select>
        <select
          value={selectedPair}
          onChange={(e) => setSelectedPair(e.target.value)}
          className='appearance-none bg-[#111] border border-[#2a2a2a] text-gray-300 rounded-lg px-4 py-2.5 outline-none focus:border-[#4E8FC6]'
          disabled={!selectedExchange}
        >
          <option value=''>Select Pair</option>
          {pairs.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* --- KPI GRID --- */}
      {selectedExchange && selectedPair && Object.keys(activeKpis).length > 0 ? (
        <>
          
<h2 className='text-xl font-semibold text-center mt-10 text-[#8ab9e6] tracking-wide drop-shadow-[0_0_4px_#4E8FC6]'>
  KPIs for {selectedExchange} / {selectedPair}
</h2>

{/* --- META INFO BOX --- */}
{activeMeta && (
  <div className='mt-3 mb-6 text-center text-sm text-gray-400'>
    <div>
      Capital Used:{' '}
      <span className='text-gray-200'>
        {activeMeta.capitalUsed?.toFixed?.(8) || '–'}
      </span>
    </div>
    <div>
      Realized PnL:{' '}
      <span className='text-green-400'>
        {activeMeta.realizedPnL?.toFixed?.(8) || '–'}
      </span>
    </div>
    <div>
      Active Days:{' '}
      <span className='text-gray-200'>
        {activeMeta.activeDays?.toFixed?.(1) || '–'}
      </span>
    </div>
    <div>
      Period:{' '}
      <span className='text-gray-300'>
        {activeMeta.minT
          ? new Date(activeMeta.minT).toISOString().slice(0, 10)
          : '–'}{' '}
        →{' '}
        {activeMeta.maxT
          ? new Date(activeMeta.maxT).toISOString().slice(0, 10)
          : '–'}
      </span>
    </div>
  </div>
)}

<div className='text-center mb-3'>
  <button
    onClick={() => {
      localStorage.removeItem(storedOrderKey)
      setKpiOrder(Object.keys(activeKpis))
    }}
    className='text-xs text-gray-400 hover:text-[#4E8FC6] transition-all'
  >
    Reset Layout
  </button>
</div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10'>
            {kpiOrder.map((key) => {
              const raw = activeKpis[key]
              const valueNum = parseFloat(String(raw).replace(/[%]/g, ''))
              const isPercent = String(raw).includes('%')
              const formattedValue = isPercent
                ? `${parseFloat(valueNum.toFixed(1))}%`
                : Number.isFinite(valueNum)
                ? parseFloat(valueNum.toFixed(2)).toString()
                : String(raw)

              const min = 0
              const max = isPercent ? 100 : Math.max(5, Math.ceil(valueNum))
              const step = (max - min) / 5
              const ticks = Array.from({ length: 6 }, (_, i) => ({
                value: min + i * step,
                label: (min + i * step).toFixed(0),
              }))

              const tooltipText =
                KPI_DESCRIPTIONS[key] ||
                `KPI "${key}" — specific performance metric for ${selectedPair}.`

              return (
                <div
                  key={key}
                  draggable
                  onDragStart={(e) => onDragStart(e, key)}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, key)}
                  className='group relative bg-[#1F1F1F] border border-[#1a1a1a] rounded-2xl 
                             p-6 flex flex-col items-center justify-center transition-all 
                             duration-300 cursor-grab hover:border-[#4E8FC6] hover:shadow-[0_0_15px_#4E8FC620]'
                >
                  {/* Tooltip */}
                  <div
                    className='absolute bottom-full mb-2 hidden group-hover:block 
                               bg-black text-gray-200 text-xs py-2 px-3 rounded-lg 
                               whitespace-pre-line w-56 text-center border border-[#2a2a2a] shadow-lg'
                  >
                    {tooltipText}
                  </div>

                  <GaugeComponent
                    value={valueNum}
                    minValue={min}
                    maxValue={max}
                    type='radial'
                    marginInPercent={{ top: 0.07, bottom: 0, left: 0.07, right: 0.07 }}
                    labels={{
                      valueLabel: {
                        formatTextValue: () => formattedValue,
                        style: {
                          fill: '#FFFFFF',
                          fontSize: '20px',
                          fontWeight: 600,
                          textAnchor: 'middle',
                          dominantBaseline: 'middle',
                        },
                      },
                      tickLabels: {
                        type: 'inner',
                        ticks,
                        defaultTickValueConfig: {
                          style: { color: 'white', fontSize: '14px' },
                          formatTextValue: (v: number) =>
                            ticks.find((t) => t.value === v)?.label ?? '',
                        },
                      },
                    }}
                    arc={{
                      colorArray: BLUE_SCALE,
                      width: 0.15,
                      padding: 0.03,
                      cornerRadius: 7,
                      gradient: false,
                      subArcs: ticks.map((t) => ({
                        limit: Math.min(Math.max(t.value, min), max),
                      })),
                    }}
                    pointer={{
                      elastic: true,
                      color: ACCENT,
                      type: 'needle',
                      width: 15,
                      length: 0.5,
                    }}
                  />

                  <div className='mt-3 text-base md:text-lg font-medium text-gray-300 tracking-wide text-center'>
                    {key.replace(/([A-Z])/g, ' $1')}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <div className='text-center text-gray-500 mt-12'>Select exchange and pair to view KPIs</div>
      )}
    </div>
  )
}
